import { Component, effect, inject, input, QueryList, untracked, ViewChildren } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DateRangeCalendarEventsListStore } from '@modules/calendar-app/services/date-range-events-list.store';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { type CalendarType } from '@modules/calendar-app/types/calendar-type';
import { EventsListSortField } from '@modules/calendar-app/types/events-list-state';
import { type ItemsResponse } from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type SelectableListChain } from '@modules/drag-n-drop/types/selectable-list-chain';
import {
  addDaysInZone,
  beginningOfDayInZone,
  beginningOfPeriodInZone,
  dayMathZone,
  endOfDayInZone,
  endOfPeriodInZone,
} from '@modules/calendar-app/utils/display-zone-period.util';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { getCurrentTimezone } from '@frontendlib/common/utils/get-current-timezone';
import { fakeLocalToUtc } from '@modules/form-controls/types/timezone-convert';
import { beginningOfDay, isSameDay, maxDate, minDate } from '@sharedlib/common/utils/date';
import type { Functions } from '@sharedlib/common/lang/Functions';
import { isValidTimeZone } from '@sharedlib/common/utils/timezone';
import { Order } from '@sharedlib/common/types/order.dto';
import { Sorter } from '@sharedlib/common/lang/Sorter';
import { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { BehaviorSubject, delay, Observable, skip, takeUntil } from 'rxjs';
import { type EventListItem, EventsListComponent } from './events-list.component';

interface EventStitchListItem extends EventListItem {
  key: string;
  /**
   * The value the day HEADING is derived from, in the section frame: a floating carrier for an all-day
   * event (never projected), a real instant for a timed one.
   */
  sortStartDate: Date;
  sortEndDate: Date;
  /**
   * The value the list is ORDERED by — always a real instant, so every row is comparable (SBUX-829).
   *
   * Ordering and headings need different values, which is why both exist. Sorting on `sortStartDate`
   * compared a floating carrier against an instant: an all-day event on day D carries browser-local
   * midnight of D, which as an instant is the account zone's midnight ± the offset gap, so it sorted
   * either side of a timed event on the neighbouring day and the list read Today → Tomorrow → Today.
   * For an all-day event this is the ACCOUNT zone's start/end of its calendar date.
   */
  orderStart: Date;
  orderEnd: Date;
}

@Component({
  selector: 'app-time-range-events-list',
  templateUrl: './time-range-events-list.component.html',
  styleUrls: ['./events-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class TimeRangeEventsListComponent extends EventsListComponent {
  protected readonly dateRangeCalendarEventsListStore = inject(DateRangeCalendarEventsListStore);
  protected readonly dateFormatService = inject(DateFormatService);
  /** The chain the sticky list above this one opens (see `SelectableListChain`). */
  public readonly selectionChain = input<SelectableListChain<CalendarEventSummaryDto> | null>(null);
  protected anyEventsFiltered: boolean;

  /**
   * The frame `item.section` values live in. `convertToItems` maps each section into a browser-local
   * Date carrying the intended display day (fake-local), so the day separator must read it back in
   * the runtime zone — projecting it into the account zone again would shift the label a day.
   */
  protected readonly sectionFrameTimeZone = getCurrentTimezone();

  @ViewChildren(ListItemSeparatorComponent)
  public separators: QueryList<ListItemSeparatorComponent>;

  public override items: EventStitchListItem[] = [];

  constructor() {
    super();

    effect((): void => {
      const scrollToDate = this.dateRangeCalendarEventsListStore.scrollToDate();
      if (scrollToDate) {
        untracked((): void => {
          this.scrollToDateHandler(scrollToDate);
        });
      }
    });

    let prevSelectedDate = this.dateRangeCalendarEventsListStore.selectedDate();
    effect((): void => {
      const selectedDate = this.dateRangeCalendarEventsListStore.selectedDate();
      if (selectedDate.getTime() !== prevSelectedDate.getTime()) {
        prevSelectedDate = selectedDate;
        untracked((): void => {
          const scrollToDate = this.dateRangeCalendarEventsListStore.scrollToDate();
          if (scrollToDate && scrollToDate.getTime() === selectedDate.getTime()) {
            return;
          }
          this.scrollAfterNavigation(selectedDate, this.dateRangeCalendarEventsListStore.calendarType());
        });
      }
    });

    let prevCalendarType = this.dateRangeCalendarEventsListStore.calendarType();
    effect((): void => {
      const calendarType = this.dateRangeCalendarEventsListStore.calendarType();
      if (calendarType !== prevCalendarType) {
        prevCalendarType = calendarType;
        untracked((): void => {
          this.scrollAfterNavigation(this.dateRangeCalendarEventsListStore.selectedDate(), calendarType);
        });
      }
    });
  }

  protected override subscribeToRefresh(): void {
    // The rows come from the store, which reloads on its own; there is nothing to re-fetch here. A re-click
    // on the open folder does what this list's refresh button does: back to today.
    this.calendarAppStateService
      .getVirtualFolderReselections()
      .pipe(skip(1), takeUntil(this.alive))
      .subscribe(() => this.refreshView());
  }

  private readonly dataLoad$ = toObservable(this.dateRangeCalendarEventsListStore.events);

  protected override handleDataLoad(): Observable<ItemsResponse<CalendarEventSummaryDto>> {
    return this.dataLoad$;
  }

  protected override getItems(_offset: number, _limit: number): Observable<ItemsResponse<CalendarEventSummaryDto>> {
    throw new Error('Method not implemented.');
  }

  protected refreshView(andScrollTo: boolean = true): void {
    const currentDate = new Date();
    this.dateRangeCalendarEventsListStore.setSelectedDate(currentDate, andScrollTo ? currentDate : null);
  }

  private getSeparatorForDay(date: Date): ListItemSeparatorComponent | undefined {
    // SBUX-829 (bug B): separator values are fake-local Dates in the display zone (see convertToItems),
    // so the incoming instant must be mapped to the same frame or the same-day / ordering comparison
    // would straddle the browser↔account offset near midnight.
    const target = toDisplayZoneWallClock(date, this.dateRangeCalendarEventsListStore.displayTimeZone());
    for (const separator of this.separators) {
      const value = separator.value();
      if (value instanceof Date) {
        if (isSameDay(value, target)) {
          return separator;
        } else if (value.getTime() > target.getTime()) {
          return;
        }
      }
    }
  }

  public scrollToDateHandler(date: Date): void {
    // `currentFilters()` is legitimately null whenever the store has nothing to ask for yet — a virtual
    // folder other than "all events", list/search state not loaded, or (SBUX-829) the account timezone not
    // yet resolved. Dereferencing it unguarded threw; the sibling read at `isStartDateSort` below always
    // used `?.`, so this was the odd one out.
    if (!date || this.dateRangeCalendarEventsListStore.currentFilters()?.sortBy !== 'startTime') {
      return;
    }

    this.dateRangeCalendarEventsListStore
      .waitForLoadToFinish()
      .pipe(
        delay(0), // wait for finish of current render cycle
        takeUntil(this.alive),
      )
      .subscribe(() => {
        const scrollSeparator: ListItemSeparatorComponent | undefined = this.getSeparatorForDay(date);
        if (scrollSeparator) {
          scrollSeparator.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
  }

  private static readonly COMPARATORS = {
    startTime: {
      [Order.asc]: Sorter.byField<EventStitchListItem>('orderStart').nullsLast(false).build(),
      [Order.desc]: Sorter.byField<EventStitchListItem>('orderStart').nullsLast(false).inverse().build(),
    },
    endTime: {
      [Order.asc]: Sorter.byField<EventStitchListItem>('orderEnd').nullsLast(false).build(),
      [Order.desc]: Sorter.byField<EventStitchListItem>('orderEnd').nullsLast(false).inverse().build(),
    },
  } as const satisfies Partial<Record<EventsListSortField, unknown>>;

  protected override convertToItems(originalEvents: CalendarEventSummaryDto[]): EventListItem[] {
    // filter out pinned/snoozed/flagged events if they are on top already in own sticky list.
    // Followed events stay in the main list WHILE PENDING (follow-up must not relocate them), but
    // once the follow-up time has passed they move to the sticky list, so filter out only expired ones.
    const events = originalEvents.filter((event) => {
      if (this.filters.pinnedOnTop && event.pinned) {
        return false;
      }
      if (this.filters.followedOnTop && event.followed && event.followed.getTime() <= Date.now()) {
        return false;
      }
      if (this.filters.snoozedOnTop && event.snoozed) {
        return false;
      }
      if (this.filters.flaggedOnTop && event.flagged) {
        return false;
      }
      return true;
    });

    this.anyEventsFiltered = events.length !== originalEvents.length;

    const comparator: Functions.Comparator<EventStitchListItem> =
      TimeRangeEventsListComponent.COMPARATORS[this.filters.sortBy]?.[this.filters.sortOrder || Order.asc];
    if (comparator) {
      const fromTime = this.dateRangeCalendarEventsListStore.fromTime();
      const toTime = this.dateRangeCalendarEventsListStore.toTime();
      const displayZone = this.dateRangeCalendarEventsListStore.displayTimeZone();

      const items = events
        .map((event) => this.convertToItem(event))
        .map((item) => TimeRangeEventsListComponent.splitEventByDay(item, fromTime, toTime, displayZone))
        .flat()
        .sort(comparator)
        .map((item) => {
          // SBUX-829 (bug B): group + label the day header in the display (account) zone, consistent
          // with the event's time label. The base grouping (isSameDay) and the shared separator both
          // read browser-local getters, so mapping the real instant to a fake-local Date whose
          // browser-local wall clock equals the account-zone wall clock makes both read the account day.
          // All-day events are floating carriers, not instants — `dayMathZone` withholds the zone so
          // they are never projected (that shifted them a day; SBUX-829 / PR #608).
          const sectionInstant = this.filters.sortBy === 'startTime' ? item.sortStartDate : item.sortEndDate;
          item.section = toDisplayZoneWallClock(sectionInstant, dayMathZone(item.data.allDay, displayZone));
          return item;
        });

      return items.map((item, index) => {
        item.showSectionHeader = this.doNeedToShowGroupSeparator(item.section, items[index - 1]?.section);
        return item;
      });
    }

    return super.convertToItems(events);
  }

  protected handlePrevNextTimeSlotPagination(date: Date): void {
    this.dateRangeCalendarEventsListStore.setSelectedDate(date);
  }

  private scrollAfterNavigation(selectedDate: Date, calendarType: CalendarType): void {
    this.dateRangeCalendarEventsListStore
      .waitForLoadToFinish()
      .pipe(delay(0), takeUntil(this.alive))
      .subscribe(() => {
        const isStartDateSort = this.dateRangeCalendarEventsListStore.currentFilters()?.sortBy === 'startTime';
        if (isStartDateSort && this.isTodayInRange(selectedDate, calendarType)) {
          const separator = this.getSeparatorForDay(new Date());
          if (separator) {
            separator.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
          } else {
            this.scrollToTop();
          }
        } else {
          this.scrollToTop();
        }
      });
  }

  private isTodayInRange(selectedDate: Date, calendarType: CalendarType): boolean {
    const localeId = this.dateRangeCalendarEventsListStore.localeId();
    const timeZone = this.dateRangeCalendarEventsListStore.displayTimeZone();
    const fromTime = beginningOfPeriodInZone(selectedDate, calendarType, localeId, timeZone);
    const toTime = endOfPeriodInZone(selectedDate, calendarType, localeId, timeZone);
    const today = new Date();
    return fromTime.getTime() <= today.getTime() && toTime.getTime() >= today.getTime();
  }

  protected override getSection(item: CalendarEvent): unknown | undefined {
    // no pinned/followed/snoozed/flagged in separate sections
    return this.getSectionValue(item);
  }

  /**
   * SBUX-829 (bug B): day boundaries are reckoned in the display (account) zone, not the browser's,
   * so a multi-day event splits on the same day edges its per-segment header will be grouped under.
   * With no/invalid zone the `*InZone` helpers fall back to browser-local, preserving prior behaviour.
   *
   * An all-day event's start/end are floating carriers rather than instants, so `dayMathZone`
   * withholds the zone for them and the day edges are read off the carriers directly — projecting
   * them would split a multi-day all-day event on shifted boundaries (PR #608).
   */
  private static splitEventByDay(
    item: EventListItem,
    rangeStartDate: Date,
    rangeEndDate: Date,
    displayTimeZone: string | null | undefined,
  ): EventStitchListItem | EventStitchListItem[] {
    const displayZone = dayMathZone(item.data.allDay, displayTimeZone);
    /**
     * A floating carrier is not an instant, so it cannot be compared with one. Anchor it to the day it
     * denotes in the ACCOUNT zone; a timed value already IS the instant.
     */
    const toOrderInstant = (value: Date, edge: 'start' | 'end'): Date => {
      if (!item.data.allDay || !isValidTimeZone(displayTimeZone)) {
        return value;
      }
      const dayStart = fakeLocalToUtc(beginningOfDay(value), displayTimeZone);
      return edge === 'start' ? dayStart : endOfDayInZone(dayStart, displayTimeZone);
    };
    const startTime = maxDate(item.data.startTime, rangeStartDate);
    const endTime = minDate(item.data.endTime, rangeEndDate);
    if (!(startTime instanceof Date) || !(endTime instanceof Date) || startTime >= endTime) {
      return {
        ...item,
        key: TimeRangeEventsListComponent.makeItemKey(item.data, startTime),
        sortStartDate: startTime,
        sortEndDate: endTime,
        orderStart: toOrderInstant(startTime, 'start'),
        orderEnd: toOrderInstant(endTime, 'end'),
      };
    }

    const startOfFirstDay = beginningOfDayInZone(startTime, displayZone);
    const startOfLastDay = beginningOfDayInZone(endTime, displayZone);
    if (startOfFirstDay.getTime() === startOfLastDay.getTime()) {
      return {
        ...item,
        key: TimeRangeEventsListComponent.makeItemKey(item.data, startTime),
        sortStartDate: startTime,
        sortEndDate: endTime,
        orderStart: toOrderInstant(startTime, 'start'),
        orderEnd: toOrderInstant(endTime, 'end'),
      };
    }

    const segments: EventStitchListItem[] = [];
    for (
      let cursor = startOfFirstDay;
      cursor.getTime() <= startOfLastDay.getTime();
      cursor = addDaysInZone(cursor, 1, displayZone)
    ) {
      const isFirstDay = cursor.getTime() === startOfFirstDay.getTime();
      const isLastDay = cursor.getTime() === startOfLastDay.getTime();
      const sortStartDate = new Date(isFirstDay ? startTime : cursor);
      const sortEndDate = new Date(isLastDay ? endTime : endOfDayInZone(cursor, displayZone));

      segments.push({
        ...item,
        key: TimeRangeEventsListComponent.makeItemKey(item.data, sortStartDate),
        sortStartDate,
        sortEndDate,
        orderStart: toOrderInstant(sortStartDate, 'start'),
        orderEnd: toOrderInstant(sortEndDate, 'end'),
      });
    }

    return segments;
  }

  private static makeItemKey(event: CalendarEventSummaryDto, segmentStartDate: Date): string {
    return `${event.id}_${segmentStartDate.getTime()}`;
  }
}
